/* eslint-disable import/no-named-as-default */
/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  TableContainer,
  Typography,
  Button,
  TableCell,
  TableRow,
  Checkbox,
} from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Dropzone, Tooltip } from 'components';
import Table from 'modules/shared-elements/Table';
import StyledTableCell from 'modules/shared-elements/Table/TableCell';
import Toolbar from 'views/ProductDetail/components/Toolbar';
import { formatBytes } from 'util/file';
import useNotification from 'hooks/useNotification';
import useDownloadFile from 'hooks/useDownloadFile';
import { Description, Folder } from '@material-ui/icons';
import useSelectValue from 'hooks/useSelectValue';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddNewModal from '../Modal/AddNewTab';
import EmptyState from '../EmptyState';
import SorterByName from '../SorterByName';
import { ToolbarActions, Actions } from '../../../../constants';
import moment from 'moment';
import UpdateDateModal from '../Modal/UpdateDateModal';
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined';
import DeleteModal from '../Modal/DeleteModal';
import RenameModal from '../Modal/RenameModal';
import { buildHierarchical } from 'util/file';
import TableFiler from 'components/Filters/Table';
import { StyledButton } from 'views/AddProduct/components/Elements';
import VerifiedTooltip from '../VerifyTooltip';
import VerifyModal from '../Modal/VerifyModal';
import clsx from 'clsx';
import { ProductFileManagementContext, ProductBinManagementContext } from 'context';
import bellIcon from 'assets/img/bell.svg';
import markInactiveWhite from 'assets/img/mark_in_active_white.svg';
import MoveToModal from '../Modal/MoveToModal';

import classes from './FileManagement.module.scss';

const FileManagement = ({
  data = [],
  currentFolder = { id: 0 },
  onCreateFolder,
  onOpenFolder,
  onUploadFiles,
  onShareFile,
  onBulkDownload,
  onUpdateExpiryDate,
  onUpdateReminder,
  onDeleteFileFolder,
  onRename,
  onMoveFileFolder,
  selectToShare,
  activeTab,
  selectedShare,
  reloadData,
  files,
  isActive = true,
  onOpenMarkActiveModal,
}) => {
  const { restoreFileByIds } = React.useContext(ProductBinManagementContext);
  const { verifyFile, moveFiles, requestDoC } = React.useContext(ProductFileManagementContext);
  const [draggingId, setDraggingId] = React.useState('');
  const [dragoverId, setDragOverId] = React.useState('');
  const isUndo = React.useRef(false);
  const isMounted = React.useRef(false);
  const [isDeleting, setIsDeleting] = React.useState([]);
  const dropzoneRef = React.createRef();
  const [show, closeSnackbar] = useNotification();
  const [download] = useDownloadFile();
  const [showAddNewFolder, setShowAddNewFolder] = React.useState(false);
  const [sortName, setSortName] = React.useState('asc');
  const [filterVerified, setFilterVerified] = React.useState(['true', 'false']);
  const [selected, setSelected, isChecked, handleSelect] = useSelectValue([]);
  const previousFolder = React.useRef(currentFolder);
  const [isDrag, setIsDrag] = React.useState(false);
  const [renameModal, setRenameModal] = React.useState({
    open: false,
    type: '',
    confirm: () => {},
  });
  const [moveToModal, setMoveToModal] = React.useState({
    open: false,
    type: '',
    confirm: () => {},
  });
  const [verifyModal, setVerifyModal] = React.useState({
    open: false,
    confirm: () => {},
  });
  const [modal, setModal] = React.useState({
    open: false,
    type: '',
    confirm: () => {},
  });
  const [deleteModal, setDeleteModal] = React.useState({
    open: false,
    confirm: () => {},
  });
  const resetModal = () => {
    setModal({
      open: false,
      type: '',
      confirm: () => {},
    });
  };
  const [showMarkActiveModal, setShowMarkActiveModal] = React.useState(false);

  const folderFiles = useMemo(() => {
    return data
      .filter(({ id }) => !isDeleting.includes(`${id}`))
      .filter(({ parentId: folderFileParentId }) => currentFolder.id === (folderFileParentId || 0))
      .sort((a, b) => {
        const order = sortName === 'asc' ? 1 : -1;
        return (a.name || '').localeCompare(b.name) * order;
      });
  }, [currentFolder, data, sortName, isDeleting]);

  const getHierarchical = React.useCallback((id) => {
    return buildHierarchical(id, data);
  });

  const isSelectingFile = useMemo(() => {
    if (selected.length === 1) {
      const fileId = selected[0];
      const file = folderFiles.find(({ id }) => id == fileId);
      return !!file?.url;
    }
    return false;
  }, [selected, folderFiles]);

  const isSelectingMultipleFile = useMemo(() => {
    if (selected.length > 1) {
      const fileId = selected[0];
      const file = folderFiles.find(({ id }) => id == fileId);
      return !!file?.url;
    }
    return false;
  }, [selected, folderFiles]);

  const isSelectingFolder = useMemo(() => {
    if (selected.length === 1) {
      const fileId = selected[0];
      const file = folderFiles.find(({ id }) => id == fileId);
      return !file?.url;
    }
    return false;
  }, [selected, folderFiles]);

  const isSelectingFilesOrFolders = useMemo(() => {
    return (
      selected.length > 1 &&
      selected.some((fileId) => {
        const file = folderFiles.find(({ id }) => id == fileId);
        return !file?.url;
      })
    );
  }, [selected]);

  const countingFilesAndFoldersSelected = useMemo(() => {
    return selected.reduce(
      (acc, fileId) => {
        const file = folderFiles.find(({ id }) => id == fileId);
        if (file.url) {
          return {
            ...acc,
            totalFiles: acc.totalFiles + 1,
          };
        }
        const hierarchical = getHierarchical(file.id);
        const folder = hierarchical[file.id];
        if (!folder) return { ...acc, totalFolders: acc.totalFolders + 1 };
        return {
          files: acc.files.concat(folder?.totalFiles ?? []),
          folders: acc.folders.concat(folder?.totalFolders ?? []),
          totalFiles: acc.totalFiles + folder?.totalFiles?.length ?? 0,
          totalFolders: acc.totalFolders + 1 + folder?.totalFolders?.length ?? 0,
        };
      },
      { files: [], folders: [], totalFiles: 0, totalFolders: 0 }
    );
  }, [selected]);

  React.useEffect(() => {
    if (!selected.length) {
      selectToShare({ ...selectedShare, [activeTab]: [] });
    }
    const willShare = selected.reduce((acc, current) => {
      const file = folderFiles.find(({ id }) => `${id}` === current);
      if (file.url) {
        acc.push(file);
        return acc;
      }
      const folder = getHierarchical(file.id);
      const { totalFiles = [] } = folder[file.id] || {};
      return acc.concat(totalFiles);
    }, []);
    selectToShare({ ...selectedShare, [activeTab]: willShare });
  }, [selected]);

  const handleDeleteFileFolder = () => {
    let message = isSelectingFile ? 'File deleted' : 'Folder deleted';

    if (isSelectingMultipleFile) {
      message = `${selected.length} files deleted`;
    }

    if (isSelectingFilesOrFolders) {
      message = `${selected.length} items deleted`;
    }

    let ids = selected.map((id) => +id);
    const { files, folders } = countingFilesAndFoldersSelected;
    ids = ids.concat(folders.map(({ id }) => id));
    ids = ids.concat(files.map(({ id }) => id));
    onDeleteFileFolder({ ids });
    setIsDeleting([...selected]);
    setSelected([]);
    show('success', 'delete-success', message, {
      autoHideDuration: 8000,
      actions: (
        <>
          <Button
            type='text'
            color='primary'
            onClick={() => {
              restoreFileByIds(ids, () => {
                show('', 'undo-success', 'Action undone');
                closeSnackbar('delete-success');
                setIsDeleting([]);
                reloadData();
              });
              if (isMounted.current) setIsDeleting([]);
            }}>
            Undo
          </Button>
        </>
      ),
      preventDuplicate: true,
    });
  };

  React.useEffect(() => {
    if (previousFolder.current.id !== currentFolder.id) {
      setSelected([]);
      previousFolder.current = currentFolder;
    }
  }, [currentFolder]);

  const columnDefs = [
    {
      title: 'Name',
      key: 'name',
      name: 'name',
      dataIndex: 'name',
      width: '40%',
      size: 'medium',
      filterIcon: <ExpandMoreIcon />,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => {
        return (
          <SorterByName
            value={selectedKeys[0] || {}}
            onChange={({ key }) => {
              selectedKeys[0] = key;
              setSortName(key);
              setSelectedKeys([...selectedKeys]);
              setTimeout(() => {
                confirm();
              }, 500);
            }}
          />
        );
      },
      render: (value, record) => {
        const { url } = record;
        return (
          <Box display='flex' alignItems='center' className={classes.fileName}>
            {url ? <Description /> : <Folder />}
            <span>{value}</span>
          </Box>
        );
      },
    },
    {
      title: 'File Size',
      key: 'size',
      name: 'size',
      dataIndex: 'size',
      width: '140px',
      sorter: (a, b) => (a.size || 0) - (b.size || 0),
      render: (value, record) => {
        if (!record.url) {
          const folder = getHierarchical(record.id);
          return <>{formatBytes(folder[record.id]?.size || 0)}</>;
        }
        return <>{formatBytes(value || 0)}</>;
      },
    },
    {
      title: 'Last Modified',
      key: 'updatedAt',
      name: 'updatedAt',
      dataIndex: 'updatedAt',
      width: '140px',
      sorter: (a, b) => {
        return moment(a.updatedAt).toDate() - moment(b.updatedAt);
      },
      render: (value) => {
        return <>{moment(value).format('DD/MM/YYYY')}</>;
      },
    },
    {
      title: 'Expiry Date',
      key: 'expiryDate',
      name: 'expiryDate',
      dataIndex: 'expiryDate',
      width: '140px',
      sorter: (a, b) => moment(a.expiryDate).toDate() - moment(b.expiryDate).toDate(),
      render: (value) => {
        return value ? <span>{moment(value).format('DD/MM/YYYY')}</span> : <></>;
      },
    },
    {
      title: 'Reminder',
      key: 'reminderDate',
      name: 'reminderDate',
      dataIndex: 'reminderDate',
      sorter: (a, b) => moment(a.reminderDate).toDate() - moment(b.reminderDate).toDate(),
      render: (value, record) => {
        return value ? (
          <Box display='flex' alignItems='center' className={classes.reminder}>
            <span>{moment(value).format('DD/MM/YYYY')}</span>
            <Tooltip title={record?.remark} arrow>
              <SmsOutlinedIcon></SmsOutlinedIcon>
            </Tooltip>
          </Box>
        ) : (
          <></>
        );
      },
    },
    {
      title: 'Review Status',
      key: 'isVerified',
      name: 'isVerified',
      dataIndex: 'isVerified',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        return (
          <TableFiler.DefaultFilter
            onApply={(selected) => {
              if (!selected.length) {
                confirm();
                return;
              }
              setSelectedKeys([[...selected.filter((value) => !!value)]]);
              confirm();
            }}
            onReset={() => {
              clearFilters();
              setSelectedKeys([['true', 'false']]);
            }}
            className={classes['ml-0']}
            disablePopover
            options={[
              {
                value: 'true',
                label: 'Verified',
              },
              { value: 'false', label: 'Not verified' },
            ]}
            selections={selectedKeys[0] || ['true', 'false']}
          />
        );
      },
      render: (value, record) => {
        return (
          <Box display='flex' alignItems='center' className={classes.reminder}>
            {value ? (
              <VerifiedTooltip
                verifiedBy={`${record.verifiedBy.first_name || ''} ${record.verifiedBy.last_name ||
                  ''}`}
                notes={record.verifyNotes}
                verifiedStatus={record.isVerified}
                verifiedAt={record.verifiedAt}
              />
            ) : (
              <StyledButton
                className={clsx(classes.verifyButton, !record.documentListId && classes.hidden)}
                buttonProps={{
                  variant: 'contained',
                  disabled: !isActive,
                  onClick: (event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setVerifyModal({
                      ...verifyModal,
                      open: true,
                      confirm: (notes, status, cb) => {
                        verifyFile(record.id, status, notes, () => {
                          show('success', 'verify-success', 'File verified');
                          setVerifyModal({ ...verifyModal, open: false });
                          if (cb) cb();
                        });
                      },
                    });
                  },
                }}
                variant='contained'
                color='primary'>
                Review
              </StyledButton>
            )}
          </Box>
        );
      },
    },
  ];
  const handleAction = ((key) => () => {
    switch (key) {
      case 'new-folder':
        setShowAddNewFolder(true);
        break;
      case 'upload':
        dropzoneRef.current.open();
        break;
      case 'share':
        onShareFile();
        break;
      case 'bulk-download':
        onBulkDownload();
        break;
      case 'download':
        if (isSelectingFile) {
          const file = folderFiles.find(({ id }) => id == selected[0]);
          file && download(file.name, file.url);
          return;
        }
        onBulkDownload();
        break;
      case 'rename':
        if (isSelectingFile || isSelectingFolder) {
          const file = folderFiles.find(({ id }) => id == selected[0]);
          setRenameModal({
            open: true,
            type: isSelectingFile ? 'file' : 'folder',
            initialValue: isSelectingFile
              ? file.name.substr(0, file.name.lastIndexOf('.'))
              : file.name,
            confirm: (newName) => {
              const fullName = isSelectingFile
                ? `${newName}.${file.name.substr(file.name.lastIndexOf('.') + 1)}`
                : newName;
              onRename(
                isSelectingFile ? file.documentListId : selected[0],
                {
                  [isSelectingFile ? 'fileName' : 'name']: fullName,
                },
                (res) => {
                  setRenameModal({
                    open: false,
                    type: '',
                    confirm: () => {},
                    initialValue: '',
                  });
                },
                isSelectingFile
              );
            },
          });
        }
        break;
      case 'get-doc':
        {
          setDeleteModal({
            content:
              'Would you like to generate a Declaration of Conformity (DoC) for the selected file? It takes 3 days to complete.',
            open: true,
            confirm: () => {
              requestDoC(selected, () => {
                show('success', 'move-success', `Request sent`);
                setDeleteModal({ open: false });
              });
            },
            actionDeleteName: 'Send request',
            isDanger: false,
            title: 'Get DoC ?',
          });
        }
        break;
      case 'set-expiry-date':
        setModal({
          open: true,
          type: 'expireDate',
          confirm: (date, parsedDate, note, reset) => {
            onUpdateExpiryDate(
              {
                expiryDate: date ? moment(date).format('YYYY-MM-DD') : null,
                fileId: +selected[0],
              },
              () => {
                resetModal();
                reset();
              }
            );
          },
        });
        break;
      case 'set-reminder':
        const file = folderFiles.find(({ id }) => id == selected[0]);
        setModal({
          open: true,
          type: 'reminder',
          defaultValue: file && file.reminderDate ? moment(file.reminderDate).toDate() : new Date(),
          confirm: (date, parsedDate, note, reset) => {
            onUpdateReminder(
              {
                reminderDate: date ? moment(date).format('YYYY-MM-DD') : null,
                remark: note,
                fileId: +selected[0],
              },
              () => {
                resetModal();
                reset();
              }
            );
          },
        });
        break;
      case 'delete':
        if (isSelectingFolder) {
          const folders = getHierarchical(+selected[0]);
          if (folders[selected[0]]?.size > 0) {
            setDeleteModal({
              open: true,
              confirm: handleDeleteFileFolder,
              actionDeleteName: 'Delete folder',
              isDanger: true,
            });
            return;
          }
        }
        if (!isSelectingMultipleFile && isSelectingFilesOrFolders) {
          setDeleteModal({
            open: true,
            confirm: handleDeleteFileFolder,
            actionDeleteName: 'Delete all',
            isDanger: true,
          });
          return;
        }
        handleDeleteFileFolder();
        break;
      case 'move-to':
        setMoveToModal({
          open: true,
          confirm: (selected, parentFolderId, baseFolder) => {
            onMoveFileFolder(
              {
                fileIds: selected,
                data: {
                  parentId: parentFolderId,
                  type: baseFolder,
                },
              },
              (res) => {
                setMoveToModal({
                  open: false,
                  confirm: () => {},
                });
              }
            );
          },
        });
        break;
      default:
        console.warning('Action has not implemented yet');
        break;
    }
  });
  const shouldHiddenAction = (key) => {
    let actions = Actions['default'];
    if (isSelectingFile) {
      actions = Actions.singleFile;
    }

    if (isSelectingFolder) {
      actions = [...Actions.multipleFilesOrFolder];
      actions.push('rename');
    }

    if (isSelectingFilesOrFolders || isSelectingMultipleFile) {
      actions = Actions.multipleFilesOrFolder;
    }

    if (selected.length) {
      const notContainFolder = selected.every((fileId) => {
        const file = folderFiles.find(({ id }) => id == fileId);
        return !!file?.url;
      });
      if (!notContainFolder) {
        actions = actions.filter((command) => command !== 'get-doc');
      }
    }
    return !actions.includes(key);
  };

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Box className={classes.wrapper}>
      {isActive ? (
        <Toolbar className={classes.toolbar}>
          <Box>
            {ToolbarActions.map((action) => {
              const shouldShowEditReminder = selected.every((fileId) => {
                const file = folderFiles.find(({ id }) => id == fileId);
                return !!file?.url && !!file.reminderDate;
              });
              return (
                <Toolbar.Action
                  className={{ [classes.hidden]: shouldHiddenAction(action.key) }}
                  key={action.key}
                  onClick={handleAction(action.key)}
                  leftIcon={action.icon}>
                  {shouldShowEditReminder && action.key === 'set-reminder'
                    ? 'Edit reminder'
                    : action.label}
                </Toolbar.Action>
              );
            })}
          </Box>
          {!!selected.length && <span>{`${selected.length} selected`}</span>}
        </Toolbar>
      ) : (
        <Box className={classes.inActiveFunctionBar}>
          <Box display='flex' alignItems='center'>
            <img
              src={bellIcon}
              alt='bell-icon'
              style={{
                marginRight: 12,
              }}
            />
            <Typography
              style={{
                color: '#3A4861',
                fontSize: 14,
                fontStyle: 'normal',
                lineHeight: '18px',
                letterSpacing: '0.002em',
              }}>
              All functions are locked because the product profile is currently inactive
            </Typography>
          </Box>
          <Button className={classes.markButton} onClick={() => onOpenMarkActiveModal(true)}>
            <img
              src={markInactiveWhite}
              alt='markInactiveWhite'
              style={{
                marginRight: 4,
              }}
            />
            Reactivate profile
          </Button>
        </Box>
      )}

      <Dropzone
        disabled={!isActive}
        accept='image/*,application/*,video/*'
        noClick
        ref={dropzoneRef}
        onDrop={(files) => {
          setIsDrag(false);
          onUploadFiles(files);
        }}
        onDragEnter={() => {
          setIsDrag(true);
        }}>
        {({ open, getRootProps, getInputProps, ...test }) => {
          return (
            <div
              {...getRootProps({
                className: 'container-dropzone',
                onDrop: (event) => event.preventDefault(),
              })}>
              <input
                {...getInputProps()}
                onChange={(event) => {
                  onUploadFiles(event.currentTarget.files);
                }}
              />
              {!data.length ? (
                <EmptyState
                  isActive={isActive}
                  isFocused={isDrag}
                  onClick={() => {
                    open();
                  }}
                />
              ) : (
                <Box mt='24px'>
                  <TableContainer className={classes.container__table} component={Paper}>
                    <DragDropContext
                      onDragStart={(dragStart) => {
                        setDraggingId(dragStart.draggableId);
                      }}
                      onDragUpdate={({ combine }) => {
                        setTimeout(() => {
                          setDragOverId(combine ? combine?.draggableId : combine);
                        }, 250);
                      }}
                      onDragEnd={(result) => {
                        setDragOverId('');
                        setDraggingId('');
                        if (!result.combine) return;
                        const file = folderFiles.find(({ id }) => id == result.draggableId);
                        const folderCombined = folderFiles
                          .filter(({ documentListId }) => !documentListId)
                          .find(({ id }) => id === result.combine.draggableId);
                        if (!folderCombined) return;

                        if (selected.length) {
                          //Have Selected.
                          const isExisted = selected.some(
                            (id) => +id == result.combine.draggableId
                          );
                          if (isExisted) return;
                        }
                        const fileIds = selected.length
                          ? [].concat(selected)
                          : [result.draggableId];
                        moveFiles(fileIds, result.combine.draggableId, () => {
                          const total = selected.length || 1;
                          const unit = total > 1 ? 'items' : 'item';
                          show(
                            'success',
                            'move-success',
                            `${total} ${unit} to ${folderCombined.name}`
                          );
                          setSelected([]);
                        });
                      }}>
                      <Droppable droppableId='file-management' isCombineEnabled>
                        {(provided, snapshot) => {
                          return (
                            <>
                              <Table
                                {...provided.droppableProps}
                                draggablePlaceholder={provided.placeholder}
                                innerRef={provided.innerRef}
                                rowSelection={
                                  isActive && {
                                    selection: selected,
                                    onSelect: (record, newSelected) => {
                                      handleSelect(`${newSelected}`);
                                    },
                                    onSelectAll: (newSelected, selectedRows) => {
                                      setSelected(newSelected);
                                    },
                                  }
                                }
                                pagination={{
                                  total: folderFiles.length || 0,
                                }}
                                onRow={(record) => {
                                  return {
                                    renderRow: ({
                                      item,
                                      index,
                                      key,
                                      rowSelection,
                                      getRowKey,
                                      className,
                                      columns,
                                      handleSelect,
                                      isChecked,
                                      ...rowProps
                                    }) => {
                                      return (
                                        <Draggable key={key} index={index} draggableId={`${key}`}>
                                          {(provided, snapshot) => {
                                            const { url } = item;
                                            const isGhost =
                                              isChecked(`${getRowKey(record)}`) &&
                                              Boolean(draggingId) &&
                                              draggingId !== item.id;
                                            return (
                                              <TableRow
                                                ref={(ref) => {
                                                  provided.innerRef(ref);
                                                }}
                                                {...provided?.draggableProps}
                                                {...provided?.dragHandleProps}
                                                {...rowProps}
                                                className={clsx(
                                                  classes.row,
                                                  className,
                                                  snapshot.isDragging && classes.nonBackground,
                                                  isGhost && classes.ghost,
                                                  item.id === dragoverId &&
                                                    !isChecked(`${getRowKey(record)}`) &&
                                                    !item.documentListId &&
                                                    classes.dragOverRow
                                                )}
                                                key={getRowKey ? getRowKey(record) : index}>
                                                {!snapshot.isDragging ? (
                                                  <>
                                                    {rowSelection ? (
                                                      <StyledTableCell
                                                        width={40}
                                                        style={{
                                                          contentVisibility:
                                                            Boolean(draggingId) &&
                                                            !isChecked(`${getRowKey(record)}`)
                                                              ? 'hidden'
                                                              : 'visible',
                                                        }}>
                                                        <Checkbox
                                                          value={getRowKey(record)}
                                                          onChange={(event) => {
                                                            handleSelect(event.target.value);
                                                            rowSelection.onSelect(
                                                              record,
                                                              event.target.value
                                                            );
                                                          }}
                                                          onClick={(event) => {
                                                            event.stopPropagation();
                                                          }}
                                                          checked={isChecked(
                                                            `${getRowKey(record)}`
                                                          )}></Checkbox>
                                                      </StyledTableCell>
                                                    ) : (
                                                      undefined
                                                    )}
                                                    {columns.map(
                                                      (
                                                        {
                                                          render,
                                                          dataIndex,
                                                          align,
                                                          width,
                                                          sorter,
                                                          filterDropdown,
                                                          filterIcon,
                                                          onCell,
                                                          ...propsCell
                                                        },
                                                        columnIndex
                                                      ) => {
                                                        const cellProps = onCell
                                                          ? onCell(record, columnIndex)
                                                          : {};
                                                        return (
                                                          <StyledTableCell
                                                            align={align || 'left'}
                                                            width={width}
                                                            key={`${
                                                              getRowKey ? getRowKey(record) : index
                                                            }-${dataIndex}-${columnIndex}`}
                                                            {...propsCell}
                                                            {...cellProps}>
                                                            {render
                                                              ? render(record[dataIndex], record)
                                                              : record[dataIndex]}
                                                          </StyledTableCell>
                                                        );
                                                      }
                                                    )}
                                                  </>
                                                ) : (
                                                  <TableCell
                                                    className={classes.wrapperDraggingItem}>
                                                    <Box>
                                                      <span className={classes.draggingItem}>
                                                        {url ? <Description /> : <Folder />}
                                                        <span>
                                                          {`Move ${rowSelection.selection.length ||
                                                            1} items`}
                                                        </span>
                                                      </span>
                                                    </Box>
                                                  </TableCell>
                                                )}
                                              </TableRow>
                                            );
                                          }}
                                        </Draggable>
                                      );
                                    },
                                    onClick: () => {
                                      if (isActive) {
                                        if (!record.url) {
                                          onOpenFolder(record);
                                          return;
                                        }
                                        window.open(record.previewUrl, '_blank').focus();
                                      }
                                    },
                                  };
                                }}
                                className={classes.table}
                                columns={columnDefs}
                                dataSource={folderFiles}
                                getRowKey={(record) => record?.id}
                              />
                            </>
                          );
                        }}
                      </Droppable>
                    </DragDropContext>
                  </TableContainer>
                </Box>
              )}
            </div>
          );
        }}
      </Dropzone>

      <AddNewModal
        title='Create New Folder'
        open={showAddNewFolder}
        handleClose={() => {
          setShowAddNewFolder(false);
        }}
        placeholder='Name your folder'
        createNew={(newFolder) => {
          onCreateFolder(newFolder, (res) => {
            if (res) {
              show('success', 'folder-created', 'Folder created');
            }
            setShowAddNewFolder(false);
          });
        }}
      />

      <UpdateDateModal
        open={modal.open}
        defaultValue={modal?.defaultValue ?? new Date()}
        isReminder={modal.type === 'reminder'}
        handleSubmit={modal.confirm}
        handleClose={(reset) => {
          setModal({ open: false, type: '' });
          reset();
        }}></UpdateDateModal>

      <DeleteModal
        onDelete={() => {
          deleteModal.confirm();
          setDeleteModal({ open: false });
        }}
        actionDeleteName={deleteModal.actionDeleteName}
        open={deleteModal.open}
        isDanger={deleteModal.isDanger}
        handleClose={() => {
          setDeleteModal({
            open: false,
            confirm: () => {},
            isDanger: true,
          });
        }}
        body={
          <Box className={classes.messageDelete}>
            <Typography variant='body1'>
              {deleteModal.content ||
                'You’ll also delete all items in the selected folder, including:'}
            </Typography>
            {!deleteModal.content ? (
              <ul>
                <li>
                  {countingFilesAndFoldersSelected.totalFolders}
                  <span> folders</span>
                </li>
                <li>
                  {countingFilesAndFoldersSelected.totalFiles}
                  <span> files</span>
                </li>
              </ul>
            ) : null}
          </Box>
        }
        title={deleteModal.title || `Delete ${selected.length} items`}></DeleteModal>

      <RenameModal
        open={renameModal.open}
        title={`Rename ${renameModal.type === 'file' ? 'File' : 'Folder'}`}
        initialValue={renameModal.initialValue || ''}
        handleSubmit={renameModal.confirm}
        handleClose={(reset) => {
          setRenameModal({ open: false, type: '', initialValue: '' });
          reset();
        }}
      />

      <MoveToModal
        data={data}
        files={files}
        selected={selected}
        activeTab={activeTab}
        initialValue={currentFolder}
        open={moveToModal.open}
        title={`Move ${selected.length} ${selected.length > 1 ? 'items' : 'item'}`}
        handleSubmit={moveToModal.confirm}
        handleClose={(reset) => {
          setMoveToModal({ open: false, type: '', initialValue: '' });
          reset();
        }}
      />

      <VerifyModal
        open={verifyModal.open}
        handleClose={(reset) => {
          setVerifyModal({ open: false });
          reset();
        }}
        handleSubmit={verifyModal.confirm}
      />
    </Box>
  );
};

export default FileManagement;